import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useKid } from "@/context/KidContext";
import { toast } from "sonner";
import { Field } from "@/pages/Login";
import { Plus, Pencil, Trash2, Copy, Upload, Users as UsersIcon, BookMarked, FolderKanban, Tags, CheckSquare, Square } from "lucide-react";
import { JourneysManager, MissionsManager } from "@/pages/AdminExtras";
import { ContentImporter, TagsManager } from "@/pages/AdminImporter";

const EMPTY = {
  title: "", description: "", category: "historias", theme: "", age_groups: [],
  duration_min: 10, objective: "", body: "", materials: [], instructions: "",
  cover_image: "", printable_file: "", status: "rascunho", featured: false,
  points_awarded: "", stars_awarded: "",
  verse: "", difficulty: "", format: "", collection: "",
  audio_url: "", video_url: "", external_media_url: "",
  series_key: "", season: "", episode_number: "", related_activity_id: "",
};

const FORMATS = ["Texto", "Atividade", "História", "PDF", "Áudio", "Vídeo", "Jogo", "Devocional", "Plano de aula"];

export default function Admin() {
  const { taxonomy } = useKid();
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8 pb-16">
      <div className="flex items-center gap-3">
        <span className="text-3xl">🛠️</span>
        <div>
          <h1 className="text-3xl font-extrabold text-[#243B53]">Painel Administrativo</h1>
          <p className="text-[#243B53]/70 text-sm">Gerencie conteúdos, usuários, categorias e temas.</p>
        </div>
      </div>
      <div className="mt-3 h-1.5 rounded-full rainbow-strip opacity-90" />

      <div className="mt-6 flex gap-2 flex-wrap">
        {[["dashboard","Dashboard"],["contents","Conteúdos"],["import","Importar"],["tags","Tags"],["series","Séries"],["journeys","Jornadas"],["missions","Missões"],["users","Usuários"],["categories","Categorias"],["themes","Temas"],["reports","Reportes"]].map(([k, l]) => (
          <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-full font-bold text-sm ${tab === k ? "bg-[#FFD54F] text-[#243B53]" : "bg-white border border-[#243B53]/10"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "dashboard" && <Dashboard onGo={setTab} />}
        {tab === "contents" && <Contents taxonomy={taxonomy} />}
        {tab === "import" && <ContentImporter />}
        {tab === "tags" && <TagsManager />}
        {tab === "series" && <SeriesManager />}
        {tab === "journeys" && <JourneysManager />}
        {tab === "missions" && <MissionsManager />}
        {tab === "users" && <Users />}
        {tab === "categories" && <TaxonomyManager kind="categories" />}
        {tab === "themes" && <TaxonomyManager kind="themes" />}
        {tab === "reports" && <ReportsManager />}
      </div>
    </div>
  );
}

function Dashboard({ onGo }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then(({ data }) => setStats(data)); }, []);
  if (!stats) return <div>Carregando…</div>;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card label="Usuários" value={stats.users} emoji="👥" />
        <Card label="Crianças" value={stats.children} emoji="🧒" />
        <Card label="Conteúdos" value={stats.contents_total} emoji="📚" />
        <Card label="Publicados" value={stats.published} emoji="🌟" />
        <Card label="Rascunhos" value={stats.drafts} emoji="✍️" />
        <Card label="Conclusões" value={stats.completions} emoji="✅" />
      </div>

      <div>
        <h2 className="text-lg font-extrabold text-[#243B53] mb-3">Atalhos</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Shortcut testid="shortcut-new-content" onClick={() => onGo("contents")} icon={<Plus size={20} />} title="+ Novo conteúdo" desc="Comece um novo conteúdo agora" />
          <Shortcut testid="shortcut-manage-contents" onClick={() => onGo("contents")} icon={<BookMarked size={20} />} title="Gerenciar conteúdos" desc="Editar, publicar e duplicar" />
          <Shortcut testid="shortcut-manage-users" onClick={() => onGo("users")} icon={<UsersIcon size={20} />} title="Gerenciar usuários" desc="Ver responsáveis e admins" />
          <Shortcut testid="shortcut-manage-series" onClick={() => onGo("series")} icon={<span>📺</span>} title="Gerenciar séries" desc="Cadastre séries e episódios" />
          <Shortcut testid="shortcut-manage-categories" onClick={() => onGo("categories")} icon={<FolderKanban size={20} />} title="Gerenciar categorias" desc="12 categorias iniciais" />
          <Shortcut testid="shortcut-manage-themes" onClick={() => onGo("themes")} icon={<Tags size={20} />} title="Gerenciar temas" desc="11 temas iniciais" />
        </div>
      </div>
    </div>
  );
}

function Shortcut({ icon, title, desc, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className="card-soft p-5 text-left hover:-translate-y-1 transition">
      <div className="w-10 h-10 rounded-full bg-[#FFD54F] text-[#243B53] flex items-center justify-center">{icon}</div>
      <div className="mt-3 font-extrabold text-[#243B53]">{title}</div>
      <div className="text-sm text-[#243B53]/60">{desc}</div>
    </button>
  );
}

function Card({ label, value, emoji }) {
  return (
    <div className="card-soft p-5">
      <div className="text-3xl">{emoji}</div>
      <div className="mt-2 text-[#243B53]/60 text-xs font-bold">{label}</div>
      <div className="font-extrabold text-3xl text-[#243B53]">{value}</div>
    </div>
  );
}

function Users() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setUsers(data.users)); }, []);
  return (
    <div className="card-soft overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-[#FFF9ED] text-left"><tr>
          <th className="p-3">Nome</th><th className="p-3">E-mail</th><th className="p-3">Papel</th><th className="p-3">Criado</th>
        </tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-t border-[#243B53]/5" data-testid={`admin-user-${u.id}`}>
              <td className="p-3 font-bold">{u.name}</td>
              <td className="p-3">{u.email}</td>
              <td className="p-3"><span className="chip capitalize">{u.role}</span></td>
              <td className="p-3 text-xs text-[#243B53]/60">{u.created_at?.slice(0,10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Contents({ taxonomy }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [batchAction, setBatchAction] = useState("publish");
  const [batchValue, setBatchValue] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/contents");
    setItems(data.contents);
    setSelected(new Set());
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Excluir este conteúdo?")) return;
    await api.delete(`/admin/contents/${id}`);
    await load(); toast.success("Excluído");
  };
  const duplicate = async (id) => {
    await api.post(`/admin/contents/${id}/duplicate`);
    await load(); toast.success("Duplicado como rascunho");
  };

  const toggleSel = (id) => setSelected((s) => {
    const ns = new Set(s);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    return ns;
  });
  const selectAllVisible = (list) => setSelected((s) => {
    const ns = new Set(s);
    const allSelected = list.every((c) => ns.has(c.id));
    if (allSelected) list.forEach((c) => ns.delete(c.id));
    else list.forEach((c) => ns.add(c.id));
    return ns;
  });

  const runBatch = async () => {
    if (selected.size === 0) return toast.error("Selecione ao menos 1 conteúdo");
    const body = { ids: Array.from(selected), action: batchAction };
    if (batchAction === "set_category") body.value = batchValue;
    if (batchAction === "set_premium" || batchAction === "set_featured") body.boolean_value = batchValue === "true";
    try {
      const { data } = await api.post("/admin/contents/batch", body);
      toast.success(`${data.modified}/${data.matched} atualizados${data.invalid_ids?.length ? ` — ${data.invalid_ids.length} inválidos` : ""}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const visible = items.filter((c) => !filter || c.title.toLowerCase().includes(filter.toLowerCase()) || c.category.toLowerCase().includes(filter.toLowerCase()));

  return (
    <>
      <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
        <input data-testid="admin-content-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrar por título ou categoria"
          className="rounded-full border border-[#243B53]/15 bg-white px-4 py-2 text-sm w-full sm:w-72" />
        <div className="flex gap-2 items-center">
          <div className="text-sm text-[#243B53]/60">{visible.length} conteúdo(s)</div>
          <button data-testid="admin-new-content-btn" onClick={() => setEditing("new")} className="btn-primary text-sm inline-flex items-center gap-2"><Plus size={16}/> Novo</button>
        </div>
      </div>
      <div className="card-soft overflow-x-auto">
        {selected.size > 0 && (
          <div className="p-3 bg-[#FFF9ED] flex flex-wrap items-center gap-2 border-b" data-testid="admin-batch-bar">
            <span className="font-bold text-sm">{selected.size} selecionado(s)</span>
            <select value={batchAction} onChange={(e) => setBatchAction(e.target.value)} data-testid="admin-batch-action"
                    className="rounded-full border border-[#243B53]/15 bg-white px-3 py-1.5 text-sm">
              <option value="publish">Publicar</option>
              <option value="unpublish">Despublicar</option>
              <option value="archive">Arquivar</option>
              <option value="set_category">Alterar categoria</option>
              <option value="set_premium">Definir premium</option>
              <option value="set_featured">Definir destaque</option>
            </select>
            {batchAction === "set_category" && (
              <select value={batchValue} onChange={(e) => setBatchValue(e.target.value)} data-testid="admin-batch-value"
                      className="rounded-full border border-[#243B53]/15 bg-white px-3 py-1.5 text-sm">
                <option value="">Escolha…</option>
                {(taxonomy?.categories || []).map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            )}
            {(batchAction === "set_premium" || batchAction === "set_featured") && (
              <select value={batchValue} onChange={(e) => setBatchValue(e.target.value)} data-testid="admin-batch-bool"
                      className="rounded-full border border-[#243B53]/15 bg-white px-3 py-1.5 text-sm">
                <option value="">Escolha…</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </select>
            )}
            <button data-testid="admin-batch-apply" onClick={runBatch} className="btn-primary text-sm">Aplicar</button>
            <button onClick={() => setSelected(new Set())} className="btn-outline text-sm">Limpar</button>
          </div>
        )}
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#FFF9ED] text-left"><tr>
            <th className="p-3 w-8">
              <button data-testid="admin-select-all-btn" onClick={() => selectAllVisible(visible)}>
                {visible.every((c) => selected.has(c.id)) && visible.length > 0 ? <CheckSquare size={16}/> : <Square size={16}/>}
              </button>
            </th>
            <th className="p-3">Título</th><th className="p-3">Categoria</th><th className="p-3">Formato</th><th className="p-3">Pts/Est</th><th className="p-3">Status</th><th className="p-3">Ações</th>
          </tr></thead>
          <tbody>
            {visible.map((c) => (
              <tr key={c.id} className="border-t border-[#243B53]/5" data-testid={`admin-row-${c.id}`}>
                <td className="p-3">
                  <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} data-testid={`admin-select-${c.id}`}/>
                </td>
                <td className="p-3 font-bold text-[#243B53]">{c.title}{c.featured ? " ⭐" : ""}{c.premium ? " 🔒" : ""}</td>
                <td className="p-3 capitalize">{c.category}</td>
                <td className="p-3 text-xs">{c.format || "—"}</td>
                <td className="p-3 text-xs">{c.points_awarded ?? "—"}/{c.stars_awarded ?? "—"}</td>
                <td className="p-3"><span className={`chip ${c.status === "publicado" ? "chip-leaf" : c.status === "rascunho" ? "chip-sun" : "chip-gold"}`}>{c.status}</span></td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => setEditing(c)} data-testid={`admin-edit-${c.id}`} className="btn-outline !py-1 !px-3 text-xs"><Pencil size={14}/></button>
                  <button onClick={() => duplicate(c.id)} data-testid={`admin-dup-${c.id}`} className="btn-outline !py-1 !px-3 text-xs"><Copy size={14}/></button>
                  <button onClick={() => remove(c.id)} data-testid={`admin-del-${c.id}`} className="btn-outline !py-1 !px-3 text-xs text-red-600"><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ContentEditor
          initial={editing === "new" ? EMPTY : { ...EMPTY, ...editing }}
          isNew={editing === "new"}
          taxonomy={taxonomy}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

function ContentEditor({ initial, isNew, taxonomy, onClose, onSaved }) {
  const [f, setF] = useState({ ...initial, materials: initial.materials || [] });
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState("basico");
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const toggleAge = (key) => setF((s) => {
    const arr = s.age_groups.includes(key) ? s.age_groups.filter(a => a !== key) : [...s.age_groups, key];
    return { ...s, age_groups: arr };
  });

  const uploadFile = async (file, target) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      set(target)(url);
      toast.success("Upload realizado!");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        materials: Array.isArray(f.materials) ? f.materials : String(f.materials || "").split("\n").filter(Boolean),
        duration_min: Number(f.duration_min) || 10,
        points_awarded: f.points_awarded === "" || f.points_awarded == null ? null : Number(f.points_awarded),
        stars_awarded: f.stars_awarded === "" || f.stars_awarded == null ? null : Number(f.stars_awarded),
        verse: f.verse || null,
        difficulty: f.difficulty || null,
        format: f.format || null,
        collection: f.collection || null,
        audio_url: f.audio_url || null,
        video_url: f.video_url || null,
        external_media_url: f.external_media_url || null,
        series_key: f.series_key || null,
        season: f.season === "" || f.season == null ? null : Number(f.season),
        episode_number: f.episode_number === "" || f.episode_number == null ? null : Number(f.episode_number),
        related_activity_id: f.related_activity_id || null,
      };
      if (isNew) await api.post("/admin/contents", payload);
      else await api.put(`/admin/contents/${initial.id}`, payload);
      toast.success("Salvo!");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const sections = [
    ["basico", "Básico"],
    ["conteudo", "Conteúdo"],
    ["gamificacao", "Gamificação"],
    ["midia", "Mídia & Arquivos"],
    ["serie", "Série/Episódio"],
    ["extras", "Extras"],
  ];

  return (
    <div className="fixed inset-0 z-[60] bg-[#243B53]/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-w-3xl mx-auto card-soft p-6 my-6 bg-white">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-xl font-extrabold text-[#243B53]">{isNew ? "Novo conteúdo" : "Editar conteúdo"}</h3>
          <button onClick={onClose} className="btn-outline text-sm">Fechar</button>
        </div>

        <div className="flex gap-2 flex-wrap mb-5">
          {sections.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setSection(k)} data-testid={`editor-section-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold ${section === k ? "bg-[#5DADE2] text-white" : "bg-white border border-[#243B53]/10"}`}>
              {l}
            </button>
          ))}
        </div>

        {section === "basico" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Field label="Título" value={f.title} onChange={set("title")} testid="editor-title" /></div>
            <div className="md:col-span-2"><Field as="textarea" label="Descrição curta" value={f.description} onChange={set("description")} testid="editor-desc" rows={2}/></div>
            <Field as="select" label="Categoria" value={f.category} onChange={set("category")} testid="editor-category" options={taxonomy.categories.filter(c => !c.archived).map(c => ({ value: c.key, label: c.label }))} />
            <Field as="select" label="Tema" value={f.theme} onChange={set("theme")} testid="editor-theme" options={[{value:"", label:"—"}, ...taxonomy.themes.filter(t => !t.archived).map(t => ({ value: t.label, label: t.label }))]} />
            <Field label="Duração (min)" type="number" value={f.duration_min} onChange={set("duration_min")} testid="editor-duration" />
            <Field as="select" label="Status" value={f.status} onChange={set("status")} testid="editor-status" options={[
              { value: "rascunho", label: "Rascunho" },
              { value: "publicado", label: "Publicado" },
              { value: "arquivado", label: "Arquivado" },
            ]} />
            <div className="md:col-span-2">
              <div className="text-sm font-bold text-[#243B53] mb-2">Faixas etárias</div>
              <div className="flex flex-wrap gap-2">
                {taxonomy.age_groups.map((g) => (
                  <button key={g.key} type="button" data-testid={`editor-age-${g.key}`} onClick={() => toggleAge(g.key)}
                    className={`px-3 py-1.5 rounded-full text-sm font-bold ${f.age_groups.includes(g.key) ? "bg-[#FFD54F] text-[#243B53]" : "bg-white border border-[#243B53]/10"}`}>
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="md:col-span-2 flex items-center gap-2">
              <input type="checkbox" checked={f.featured} onChange={(e) => set("featured")(e.target.checked)} data-testid="editor-featured" />
              <span className="font-bold text-sm text-[#243B53]">Destacar na home</span>
            </label>
          </div>
        )}

        {section === "conteudo" && (
          <div className="grid grid-cols-1 gap-4">
            <Field label="Objetivo" value={f.objective} onChange={set("objective")} testid="editor-objective" />
            <Field as="textarea" label="Conteúdo/Corpo (histórias, devocionais, planos de aula)" value={f.body} onChange={set("body")} testid="editor-body" rows={10}/>
            <Field as="textarea" label="Materiais necessários (um por linha)" value={Array.isArray(f.materials) ? f.materials.join("\n") : f.materials} onChange={(v) => set("materials")(v.split("\n"))} testid="editor-materials" rows={3}/>
            <Field as="textarea" label="Instruções" value={f.instructions} onChange={set("instructions")} testid="editor-instructions" rows={3}/>
          </div>
        )}

        {section === "gamificacao" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Pontos concedidos (deixe em branco para o padrão)" type="number" value={f.points_awarded} onChange={set("points_awarded")} testid="editor-points" placeholder="Ex: 10" />
            <Field label="Estrelas concedidas" type="number" value={f.stars_awarded} onChange={set("stars_awarded")} testid="editor-stars" placeholder="Ex: 2" />
            <Field as="select" label="Dificuldade" value={f.difficulty} onChange={set("difficulty")} testid="editor-difficulty" options={[
              { value: "", label: "—" },
              { value: "muito_facil", label: "Muito fácil" },
              { value: "facil", label: "Fácil" },
              { value: "intermediario", label: "Intermediário" },
            ]} />
            <Field as="select" label="Formato/Tipo" value={f.format} onChange={set("format")} testid="editor-format" options={[{value:"", label:"—"}, ...FORMATS.map(x => ({ value: x, label: x }))]} />
          </div>
        )}

        {section === "midia" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FileUpload label="Imagem de capa (JPG/PNG/WEBP)" accept="image/*" onFile={(file) => uploadFile(file, "cover_image")} current={f.cover_image} testid="editor-cover-upload" />
            <FileUpload label="Arquivo para impressão (PDF)" accept="application/pdf" onFile={(file) => uploadFile(file, "printable_file")} current={f.printable_file} testid="editor-pdf-upload" />
            <Field label="URL de áudio (externa)" value={f.audio_url} onChange={set("audio_url")} testid="editor-audio-url" placeholder="https://…" />
            <Field label="URL de vídeo (externa)" value={f.video_url} onChange={set("video_url")} testid="editor-video-url" placeholder="https://…" />
            <div className="md:col-span-2"><Field label="URL de mídia externa (link)" value={f.external_media_url} onChange={set("external_media_url")} testid="editor-external-url" placeholder="https://…" /></div>
          </div>
        )}

        {section === "extras" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Field label="Versículo relacionado" value={f.verse} onChange={set("verse")} testid="editor-verse" placeholder='Ex: "Amem-se uns aos outros." — João 13:34' /></div>
            <Field label="Coleção/Série (opcional — texto livre)" value={f.collection} onChange={set("collection")} testid="editor-collection" placeholder="Ex: 7 Dias com Jesus" />
          </div>
        )}

        {section === "serie" && <SerieEpisodeSection f={f} set={set} />}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline">Cancelar</button>
          <button data-testid="editor-save-btn" onClick={save} disabled={saving} className="btn-primary">{saving ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>
    </div>
  );
}

function FileUpload({ label, accept, onFile, current, testid }) {
  return (
    <div>
      <div className="text-sm font-bold text-[#243B53]">{label}</div>
      <label className="mt-1 flex items-center gap-2 cursor-pointer btn-outline w-full">
        <Upload size={16}/> <span>Escolher arquivo</span>
        <input data-testid={testid} type="file" accept={accept} className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
      </label>
      {current && <div className="mt-2 text-xs text-[#243B53]/60 break-all">{current}</div>}
    </div>
  );
}

function SerieEpisodeSection({ f, set }) {
  const [series, setSeries] = useState([]);
  const [contents, setContents] = useState([]);
  useEffect(() => {
    api.get("/admin/series").then(({ data }) => setSeries(data.series || []));
    api.get("/admin/contents").then(({ data }) => setContents(data.contents || []));
  }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field as="select" label="Série" value={f.series_key || ""} onChange={set("series_key")} testid="editor-series-key"
        options={[{ value: "", label: "— (não é episódio)" }, ...series.map(s => ({ value: s.key, label: s.title }))]} />
      <div />
      <Field label="Temporada" type="number" value={f.season} onChange={set("season")} testid="editor-season" placeholder="Ex: 1" />
      <Field label="Número do episódio (ordem)" type="number" value={f.episode_number} onChange={set("episode_number")} testid="editor-episode-number" placeholder="Ex: 1" />
      <div className="md:col-span-2">
        <Field as="select" label="Missão relacionada (atividade opcional)" value={f.related_activity_id || ""} onChange={set("related_activity_id")} testid="editor-related-activity"
          options={[{ value: "", label: "— (nenhuma)" }, ...contents.filter(c => c.id !== f.id).map(c => ({ value: c.id, label: `${c.title} · ${c.category}` }))]} />
        <div className="mt-1 text-xs text-[#243B53]/60">A missão será exibida na página do episódio como "🎯 Missão deste episódio".</div>
      </div>
    </div>
  );
}

function SeriesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/series");
    setItems(data.series || []);
  };
  useEffect(() => { load(); }, []);

  const archive = async (key) => {
    if (!window.confirm("Desativar esta série?")) return;
    await api.delete(`/admin/series/${key}`);
    await load(); toast.success("Série desativada");
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <div className="text-sm text-[#243B53]/60">{items.length} série(s)</div>
        <button data-testid="admin-new-series-btn" onClick={() => setEditing("new")} className="btn-primary text-sm inline-flex items-center gap-2"><Plus size={16}/> Nova série</button>
      </div>
      <div className="card-soft overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#FFF9ED] text-left"><tr>
            <th className="p-3">Título</th><th className="p-3">Chave</th><th className="p-3">Episódios</th><th className="p-3">Ordem</th><th className="p-3">Status</th><th className="p-3">Ações</th>
          </tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.key} className="border-t border-[#243B53]/5" data-testid={`admin-series-row-${s.key}`}>
                <td className="p-3 font-bold text-[#243B53]">{s.title}{s.featured ? " ⭐" : ""}</td>
                <td className="p-3 text-xs">{s.key}</td>
                <td className="p-3">{s.episode_count || 0}</td>
                <td className="p-3">{s.order ?? 0}</td>
                <td className="p-3">
                  <span className={`chip ${s.status === "ativa" ? "chip-leaf" : "chip-gold"}`}>{s.status}</span>
                </td>
                <td className="p-3 flex gap-2">
                  <button onClick={() => setEditing(s)} data-testid={`admin-series-edit-${s.key}`} className="btn-outline !py-1 !px-3 text-xs"><Pencil size={14}/></button>
                  {s.status === "ativa" && <button onClick={() => archive(s.key)} data-testid={`admin-series-archive-${s.key}`} className="btn-outline !py-1 !px-3 text-xs text-red-600"><Trash2 size={14}/></button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#243B53]/60">Nenhuma série ainda.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <SeriesEditor
          initial={editing === "new" ? { key: "", title: "", description: "", cover_image: "", age_groups: [], status: "ativa", order: 0, featured: false } : editing}
          isNew={editing === "new"}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </>
  );
}

function SeriesEditor({ initial, isNew, onClose, onSaved }) {
  const { taxonomy } = useKid();
  const [f, setF] = useState({ ...initial, age_groups: initial.age_groups || [] });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const toggleAge = (key) => setF((s) => ({
    ...s,
    age_groups: s.age_groups.includes(key) ? s.age_groups.filter(a => a !== key) : [...s.age_groups, key],
  }));

  const uploadCover = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      set("cover_image")(url);
      toast.success("Capa enviada!");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!f.key || !f.title) return toast.error("Preencha chave e título.");
    setSaving(true);
    try {
      const payload = {
        ...f,
        order: Number(f.order) || 0,
      };
      if (isNew) await api.post("/admin/series", payload);
      else await api.put(`/admin/series/${initial.key}`, payload);
      toast.success("Série salva!");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-[#243B53]/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="max-w-2xl mx-auto card-soft p-6 my-6 bg-white">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-xl font-extrabold text-[#243B53]">{isNew ? "Nova série" : "Editar série"}</h3>
          <button onClick={onClose} className="btn-outline text-sm">Fechar</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Chave (slug)" value={f.key} onChange={(v) => set("key")(v.toLowerCase().replace(/[^a-z0-9_-]/g, "-"))} testid="series-editor-key" />
          <Field label="Título" value={f.title} onChange={set("title")} testid="series-editor-title" />
          <div className="md:col-span-2"><Field as="textarea" label="Descrição" value={f.description} onChange={set("description")} testid="series-editor-desc" rows={3}/></div>
          <FileUpload label="Capa da série (JPG/PNG/WEBP)" accept="image/*" onFile={uploadCover} current={f.cover_image} testid="series-editor-cover" />
          <Field label="URL da capa (alternativa)" value={f.cover_image} onChange={set("cover_image")} testid="series-editor-cover-url" placeholder="https://…" />
          <Field label="Ordem de exibição" type="number" value={f.order} onChange={set("order")} testid="series-editor-order" placeholder="0" />
          <Field as="select" label="Status" value={f.status} onChange={set("status")} testid="series-editor-status" options={[
            { value: "ativa", label: "Ativa" },
            { value: "inativa", label: "Inativa" },
          ]} />
          <div className="md:col-span-2">
            <div className="text-sm font-bold text-[#243B53] mb-2">Faixas etárias</div>
            <div className="flex flex-wrap gap-2">
              {taxonomy.age_groups.map((g) => (
                <button key={g.key} type="button" data-testid={`series-editor-age-${g.key}`} onClick={() => toggleAge(g.key)}
                  className={`px-3 py-1.5 rounded-full text-sm font-bold ${f.age_groups.includes(g.key) ? "bg-[#FFD54F] text-[#243B53]" : "bg-white border border-[#243B53]/10"}`}>
                  {g.label}
                </button>
              ))}
            </div>
          </div>
          <label className="md:col-span-2 flex items-center gap-2">
            <input type="checkbox" checked={f.featured} onChange={(e) => set("featured")(e.target.checked)} data-testid="series-editor-featured" />
            <span className="font-bold text-sm text-[#243B53]">Destacar na home</span>
          </label>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="btn-outline">Cancelar</button>
          <button data-testid="series-editor-save-btn" onClick={save} disabled={saving} className="btn-primary">{saving ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>
    </div>
  );
}

function TaxonomyManager({ kind }) {
  const isCategory = kind === "categories";
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ key: "", label: "", emoji: "📚", color: "#5DADE2" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/taxonomy");
    setItems(isCategory ? data.categories : data.themes);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.key || !form.label) return toast.error("Preencha chave e nome.");
    setSaving(true);
    try {
      const payload = isCategory ? form : { key: form.key, label: form.label };
      await api.post(`/admin/${kind}`, payload);
      setForm({ key: "", label: "", emoji: "📚", color: "#5DADE2" });
      await load(); toast.success("Adicionado!");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const archive = async (item) => {
    if (!window.confirm(`Arquivar "${item.label}"?`)) return;
    await api.delete(`/admin/${kind}/${item.key}`);
    await load(); toast.success("Arquivado");
  };

  return (
    <div className="grid gap-6 md:grid-cols-3">
      <form onSubmit={create} className="md:col-span-1 card-soft p-5 space-y-3">
        <div className="font-extrabold text-[#243B53]">Nova {isCategory ? "categoria" : "tema"}</div>
        <Field label="Chave (slug)" value={form.key} onChange={(v) => setForm(s => ({ ...s, key: v.toLowerCase().replace(/[^a-z0-9_-]/g, "") }))} testid={`${kind}-new-key`} />
        <Field label="Nome" value={form.label} onChange={(v) => setForm(s => ({ ...s, label: v }))} testid={`${kind}-new-label`} />
        {isCategory && (
          <>
            <Field label="Emoji" value={form.emoji} onChange={(v) => setForm(s => ({ ...s, emoji: v }))} testid="categories-new-emoji" />
            <Field label="Cor" value={form.color} onChange={(v) => setForm(s => ({ ...s, color: v }))} testid="categories-new-color" />
          </>
        )}
        <button data-testid={`${kind}-new-submit`} disabled={saving} className="btn-primary w-full text-sm">{saving ? "Salvando…" : "Adicionar"}</button>
      </form>

      <div className="md:col-span-2 card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFF9ED] text-left"><tr>
            <th className="p-3">Nome</th><th className="p-3">Chave</th>{isCategory && <th className="p-3">Emoji</th>}<th className="p-3">Status</th><th className="p-3">Ações</th>
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.key} className="border-t border-[#243B53]/5" data-testid={`${kind}-row-${it.key}`}>
                <td className="p-3 font-bold">{it.label}</td>
                <td className="p-3 text-xs">{it.key}</td>
                {isCategory && <td className="p-3">{it.emoji}</td>}
                <td className="p-3">{it.archived ? <span className="chip chip-gold">Arquivado</span> : <span className="chip chip-leaf">Ativo</span>}</td>
                <td className="p-3">
                  {!it.archived && <button onClick={() => archive(it)} data-testid={`${kind}-archive-${it.key}`} className="btn-outline !py-1 !px-3 text-xs text-red-600">Arquivar</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReportsManager() {
  const [reports, setReports] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const url = filter ? `/admin/reports?status=${filter}` : "/admin/reports";
      const { data } = await api.get(url);
      setReports(data.reports || []);
    } catch {
      setReports([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/reports/${id}`, { status });
      toast.success("Reporte atualizado");
      load();
    } catch {
      toast.error("Falha ao atualizar");
    }
  };

  if (reports === null) return <div>Carregando…</div>;

  const CAT_LABEL = {
    conteudo_inadequado: "Conteúdo inadequado",
    erro_material: "Erro no material",
    problema_tecnico: "Problema técnico",
    outro: "Outro",
  };

  return (
    <div className="space-y-4" data-testid="admin-reports">
      <div className="flex items-center gap-2">
        <span className="text-sm font-bold text-[#243B53]">Filtrar:</span>
        {[["", "Todos"], ["open", "Abertos"], ["resolved", "Resolvidos"]].map(([k, l]) => (
          <button
            key={k || "all"}
            data-testid={`reports-filter-${k || "all"}`}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold ${filter === k ? "bg-[#FFD54F] text-[#243B53]" : "bg-white border border-[#243B53]/10"}`}
          >
            {l}
          </button>
        ))}
      </div>
      {reports.length === 0 ? (
        <div className="card-soft p-8 text-center text-[#243B53]/60" data-testid="reports-empty">
          Nenhum reporte {filter ? `com status "${filter}"` : "registrado"} ainda.
        </div>
      ) : (
        <div className="overflow-x-auto card-soft p-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#243B53]/60 text-xs">
                <th className="p-2">Data</th>
                <th className="p-2">Conteúdo</th>
                <th className="p-2">Categoria</th>
                <th className="p-2">Descrição</th>
                <th className="p-2">Status</th>
                <th className="p-2">Ação</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id} className="border-t border-[#243B53]/5 align-top" data-testid={`report-row-${r.id}`}>
                  <td className="p-2 text-xs text-[#243B53]/70 whitespace-nowrap">{r.created_at?.slice(0, 10)}</td>
                  <td className="p-2 font-bold text-[#243B53]">{r.content_title}</td>
                  <td className="p-2 text-[#243B53]/80">{CAT_LABEL[r.category] || r.category}</td>
                  <td className="p-2 text-[#243B53]/70 max-w-[280px]">{r.description || <span className="italic text-[#243B53]/40">(sem descrição)</span>}</td>
                  <td className="p-2">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-extrabold ${r.status === "open" ? "bg-[#FFD54F]/60 text-[#243B53]" : "bg-[#7BC67B]/30 text-[#243B53]"}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="p-2">
                    {r.status === "open" ? (
                      <button
                        data-testid={`report-resolve-${r.id}`}
                        onClick={() => updateStatus(r.id, "resolved")}
                        className="btn-outline text-xs"
                      >
                        Marcar resolvido
                      </button>
                    ) : (
                      <button
                        data-testid={`report-reopen-${r.id}`}
                        onClick={() => updateStatus(r.id, "open")}
                        className="btn-outline text-xs"
                      >
                        Reabrir
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

